import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  TableCell as MuiTableCell,
  styled,
  Tooltip,
  TablePagination,
} from "@mui/material";
import useAccessCode from "../hooks/useAccessCode";
import { IHistoryReservaciones } from "../models/codes";
import { CustomModal } from "../components/Modal";
import { Done, DoneAll, Home, Cancel } from "@mui/icons-material";
import { TableHeaderCell } from "../components/Table/TableHeaderCell";
import { TableCell } from "../components/Table/TableCell";
import { Confirm } from '../components/Confirm';



export const HistorialReserva = () => {
  const [confirm, setConfirm] = useState<boolean>(false);
  const today = new Date();
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  today.setHours(0, 0, 0, 0);

  const MobileTableCell = styled(MuiTableCell)(({ theme }) => ({
    padding: '4px 6px',
    fontSize: '0.7rem',
    lineHeight: '1.1',
    '&:first-of-type': {
      paddingLeft: '6px'
    },
    '&:last-of-type': {
      paddingRight: '6px'
    }
  }))

  const { getHistoryReservas, deleteReserva } = useAccessCode();

  const [historyR, setHistoryR] = useState<IHistoryReservaciones[]>([]);
  const [itemSelect, setItemSelect] = useState<IHistoryReservaciones>({} as IHistoryReservaciones);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => {
    setOpenModal(false);
  };

  const getStatusIcon = (item: IHistoryReservaciones) => {
    // Reserva completada (pasada)
    if (
      item.estado === 'Vigente' &&
      new Date(item.fecha_Reservacion) < new Date()
    ) {
      return {
        icon: <DoneAll fontSize="small" sx={{ color: 'blue' }} />,
        tooltip: 'Completada - No se puede anular',
      }
    }

    // Reserva pendiente (futura)
    if (
      item.estado === 'Vigente' &&
      new Date(item.fecha_Reservacion) >= new Date()
    ) {
      return {
        icon: <Done fontSize="small" sx={{ color: 'green' }} />,
        tooltip: 'Pendiente - Se puede anular',
      }
    }

    // Reserva anulada
    if (item.estado === 'Anulada') {
      return {
        icon: <Cancel fontSize="small" sx={{ color: '#FF6B6B' }} />,
        tooltip: 'Anulada'
      }
    }

    // Estado por defecto
    return {
      icon: <Done fontSize="small" color="disabled" />,
      tooltip: 'Estado desconocido'
    }
  }

  const isRowClickable = (item: IHistoryReservaciones) => {
    // Solo se puede anular si está vigente Y la fecha es futura (mayor a hoy)
    return item.estado === "Vigente" && new Date(item.fecha_Reservacion) > new Date()
  }


  useEffect(() => {
    (async () => {
      let historyReservas = await getHistoryReservas();
      if (historyReservas) {
        setHistoryR(historyReservas);
        setPage(0);
      } else {
        setModalMessage("Error al obtener el historial de reservas");
        handleOpen();
      }
    })();
  }, []);

  const AnularReserva = () => async () => {
    let amenidad = itemSelect;
    if (!amenidad) {
      return
    }
    
    // Verificar que la reserva sea futura
    if (new Date(amenidad.fecha_Reservacion) <= new Date()) {
      setModalMessage(
        "No se puede anular la reserva, la fecha de reservación ya pasó"
      );
      handleOpen();
      return;
    }

    const reservaParaAnular = {
      ...itemSelect,
      id_motivo_reserva: null
    }
    
    if (!confirm) {
      return;
    }
    
    const response = await deleteReserva(reservaParaAnular);
    if (response) {
      setModalMessage("Reserva anulada con éxito");
      handleOpen();
      setHistoryR(
        historyR.map(item =>
          item.num_Reserva === amenidad.num_Reserva
            ? { ...item, estado: "Anulada" }
            : item
        )
      );
    } else {
      setModalMessage("No es posible anular la reserva");
      handleOpen();
    }
  };



  return (
    <>
      <Confirm
        message='Desea anular la reserva seleccionada?'
        okText='Anular'
        okAction={AnularReserva()}
        show={confirm}
        setShow={setConfirm}
      />
      <Box
        textAlign="center"
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Typography variant="h4" component="div" gutterBottom marginY="1em">
          Historial de Reservas
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width={0.96}
          maxWidth={1000}
        >
          <TableContainer component={Box} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                    Amenidad
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                    #{" "}
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                    Fecha Hora{" "}
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                    Valor
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyR.length > 0 &&
                  historyR
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(item => {
                    const TableCellComponent = isSmallScreen ? MobileTableCell : MuiTableCell;
                    const status = getStatusIcon(item);

                    return (
                      <TableRow
                        key={`${item.id_Reserva}-${item.num_Reserva}-${item.fecha_Reservacion}-${item.hora}`}
                        sx={{
                          cursor: isRowClickable(item) ? "pointer" : "default",
                          backgroundColor: isRowClickable(item) ? 'rgba(1, 52, 103, 0.02)' : 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isRowClickable(item) 
                              ? 'rgba(1, 52, 103, 0.08)' 
                              : 'rgba(0, 0, 0, 0.02)',
                            transform: isRowClickable(item) ? 'translateX(2px)' : 'none'
                          }
                        }}
                        onClick={() => {
                          if (isRowClickable(item)) { 
                            setItemSelect(item); 
                            setConfirm(true); 
                          }
                        }}
                      >
                        <TableCellComponent>
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              maxWidth: '100%',
                              overflow: 'hidden'
                            }}
                          >
                            <Tooltip title={status.tooltip}>
                              {status.icon}
                            </Tooltip>
                            {item.amenidad}
                          </Box>
                        </TableCellComponent>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            padding: 1,
                          }}
                        >
                          {item.num_Reserva}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            padding: 1,
                          }}
                        >
                          {new Date(item.fecha_Reservacion).toLocaleDateString(
                            "es-ES",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "2-digit",
                            }
                          )}
                          {" "}
                          {item.hora.replace(" ", "")}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            padding: 1,
                          }}
                        >
                          Q
                          {item.valor.toString().split(".").length === 2
                            ? item.valor
                            : item.valor.toString() + ".00"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ width: "100%" }}>
            <TablePagination
              component="div"
              count={historyR.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Filas por página"
            />
          </Box>
        </Box>

        <CustomModal openState={openModal} onCloseHandler={handleClose}>
          <Typography variant="h4" component="div" gutterBottom marginY="1em">
            {modalMessage}
          </Typography>

          <Button
            color="secondary"
            variant="outlined"
            endIcon={<Home />}
            sx={{ marginY: 2, width: "50%" }}
            onClick={handleClose}
          >
            Inicio
          </Button>
        </CustomModal>
      </Box>
    </>

  );
};
